import {getUserOrgan} from './dataStorage';
import {getCurrencyByCode, getCurrencyName} from '@/config/currencyConfig';

/**
 * 获取当前机构的币种信息
 * @returns {Object} 币种信息对象
 */
export function getCurrentOrgCurrency() {
    const organInfo = getUserOrgan();

    if (organInfo && organInfo.currencyCode) {
        const currency = getCurrencyByCode(organInfo.currencyCode);
        if (currency) {
            return {
                code: organInfo.currencyCode,
                name: organInfo.currencyName || currency.name,
                symbol: organInfo.currencySymbol || currency.symbol,
                fullInfo: currency
            };
        }

        // 如果配置中没有找到，使用机构信息中的数据
        return {
            code: organInfo.currencyCode,
            name: organInfo.currencyName || organInfo.currencyCode,
            symbol: organInfo.currencySymbol || '¥',
            fullInfo: null
        };
    }

    // 默认返回人民币
    return {
        code: 'CNY',
        name: '人民币',
        symbol: '¥',
        fullInfo: getCurrencyByCode('CNY')
    };
}

/**
 * 获取当前机构的币种符号
 * @returns {string} 币种符号
 */
export function getCurrentCurrencySymbol() {
    return getCurrentOrgCurrency().symbol;
}

/**
 * 获取当前机构的币种名称
 * @param {string} language - 语言代码 (zh, en, ru, th)
 * @returns {string} 币种名称
 */
export function getCurrentCurrencyName(language = 'zh') {
    const currency = getCurrentOrgCurrency();

    if (currency.fullInfo) {
        return getCurrencyName(currency.fullInfo, language);
    }

    return currency.name;
}

/**
 * 格式化金额显示
 * @param {number|string} amount - 金额
 * @param {boolean} showSymbol - 是否显示币种符号
 * @param {number} decimals - 小数位数，默认2位
 * @returns {string} 格式化后的金额字符串
 */
export function formatCurrency(amount, showSymbol = true, decimals = 2) {
    if (amount === null || amount === undefined || amount === '') {
        return showSymbol ? `${getCurrentCurrencySymbol()}0.00` : '0.00';
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
        return showSymbol ? `${getCurrentCurrencySymbol()}0.00` : '0.00';
    }

    const formattedAmount = numAmount.toFixed(decimals);

    if (showSymbol) {
        return `${getCurrentCurrencySymbol()}${formattedAmount}`;
    }

    return formattedAmount;
}

/**
 * 获取币种单位文本（用于替换页面中的"元"字符）
 * @param {string} language - 语言代码
 * @returns {string} 币种单位文本
 */
export function getCurrencyUnit(language = 'zh') {
    const currency = getCurrentOrgCurrency();
    // 根据币种代码返回对应的单位文本
    const unitMap = {
        'CNY': {'zh-CN': '元', 'en-US': 'Yuan', 'ru-RU': 'Юань', 'th-TH': 'หยวน', 'de-DE': 'Yuan'},
        'USD': {'zh-CN': '美元', 'en-US': 'USD', 'ru-RU': 'Доллар', 'th-TH': 'ดอลลาร์', 'de-DE': 'USD'},
        'EUR': {'zh-CN': '欧元', 'en-US': 'EUR', 'ru-RU': 'Евро', 'th-TH': 'ยูโร', 'de-DE': 'EUR'},
        'JPY': {'zh-CN': '日元', 'en-US': 'Yen', 'ru-RU': 'Иена', 'th-TH': 'เยน', 'de-DE': 'Yen'},
        'GBP': {'zh-CN': '英镑', 'en-US': 'Pound', 'ru-RU': 'Фунт', 'th-TH': 'ปอนด์', 'de-DE': 'Pfund'},
        'KRW': {'zh-CN': '韩元', 'en-US': 'Won', 'ru-RU': 'Вона', 'th-TH': 'วอน', 'de-DE': 'Won'},
        'THB': {'zh-CN': '泰铢', 'en-US': 'Baht', 'ru-RU': 'Бат', 'th-TH': 'บาท', 'de-DE': 'Baht'},
        'RUB': {'zh-CN': '卢布', 'en-US': 'Ruble', 'ru-RU': 'Рубль', 'th-TH': 'รูเบิล', 'de-DE': 'Rubel'},
        'SGD': {'zh-CN': '新元', 'en-US': 'SGD', 'ru-RU': 'Сингапурский доллар', 'th-TH': 'ดอลลาร์สิงคโปร์', 'de-DE': 'SGD'},
        'HKD': {'zh-CN': '港币', 'en-US': 'HKD', 'ru-RU': 'Гонконгский доллар', 'th-TH': 'ดอลลาร์ฮ่องกง', 'de-DE': 'HKD'}
    };
    console.log(currency);

    const unit = unitMap[currency.code];
    if (unit && unit[language]) {
        return unit[language];
    }

    // 如果没有找到对应的单位，返回币种名称
    return getCurrentCurrencyName(language);
}
